import { getOrganization, isDoctorOrganization } from 'src/store/selectors';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import type { ApiClient } from 'src/store/backend';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import { assertIsDefined } from '../../assertIsDefined';
import type { ScreenView } from '../../content/screens/screen-view';
import { fetchScreen, fetchLibrary } from '../../store/signage';
import { assetAvailability } from 'src/services/asset-availability';
import { slideTemplates } from 'src/content/screens/slide-templates';
import { weekScheduleDescription } from 'src/store/selectors/employees/work-hours-description';
import { slideSuggestionsCategorizedFor, slideSuggestionsSelected } from 'src/content/screens/slide-suggestions';
import type { RssNewsItem, Slide } from 'src/content/screens/slide-element';
import type { LibraryItem } from 'src/library/screen/library-item';
import { getEmployeesPresenceStatus } from 'src/store/selectors/staffing/get-employees-presence-status';
import { mapApiOrderStatus } from 'src/content/screens/screen-order-status';
import type { ScreenOrderStatus } from 'src/content/screens/screen-view';

interface NewsFeedSnapshotItem {
  title: string;
  description: string;
  // Null when the source RSS item had no image or the download failed.
  image: string | null;
}
interface NewsFeedSnapshot {
  items: NewsFeedSnapshotItem[];
}

const SLIDE_TYPES: ReadonlySet<Slide['type']> = new Set(['custom', 'news', 'fhi', 'weather', 'image', 'employees']);

// `ScreenResult.playlistItems` er typet som `Slide[]` (se #12610), men siden
// inputen kommer fra nettverket validerer vi shape ved grensen så en server-
// endring ikke kan korrumere playlisten silent.
function toSlides(items: unknown): Slide[] {
  if (!Array.isArray(items)) return [];
  const slides: Slide[] = [];
  for (const item of items) {
    if (
      item &&
      typeof item === 'object' &&
      typeof (item as { type?: unknown }).type === 'string' &&
      SLIDE_TYPES.has((item as { type: Slide['type'] }).type) &&
      typeof (item as { id?: unknown }).id === 'string'
    ) {
      slides.push(item as Slide);
    } else {
      console.warn('[buildScreenView] Skipping playlist item with unexpected shape:', item);
    }
  }
  return slides;
}

// buildScreenView triggers on every edit, so cache the snapshot across calls.
// Backend regenerates every 15 min — 5 min client TTL keeps staleness bounded
// without hammering the endpoint.
const NEWS_CACHE_TTL_MS = 5 * 60 * 1000;
const newsCache = new Map<string, { items: RssNewsItem[]; fetchedAt: number }>();
const NEWS_DEFAULT_IMAGE_PATH = 'std/news-default/green-defocus-vr28j4wj73.jpg';

// Both NRK and FHI feeds are ingested backend-side into content-addressed S3
// snapshots (dabih-api NrkFeedIngestor / FhiFeedIngestor) — the browser only
// reads the pre-parsed `current.json`. FHI embeds the image inside its RSS
// <description> tag, but that parsing/stripping happens backend-side too, so the
// client treats both sources identically. `defaultImagePath` is optional: NRK
// falls back to a placeholder, FHI renders no image when one is missing (#13227).
async function getFeedSnapshot(
  assetBase: string,
  source: 'nrk' | 'fhi',
  defaultImagePath?: string,
): Promise<RssNewsItem[]> {
  if (!assetBase) return [];
  const cacheKey = `${assetBase}#${source}`;
  const cached = newsCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < NEWS_CACHE_TTL_MS) {
    return cached.items;
  }
  try {
    // current.json is mutable → cache-bust. Image refs inside are content-hashed
    // and can be cached forever by the browser.
    const response = await fetch(`${assetBase}/feeds/${source}/current.json?t=${Date.now()}`);
    if (!response.ok) {
      console.warn(`[getFeedSnapshot:${source}] Fetch failed with status ${response.status}`);
      return cached?.items ?? [];
    }
    const snapshot: NewsFeedSnapshot = await response.json();
    const defaultImageUrl = defaultImagePath ? `${assetBase}/${defaultImagePath}` : '';
    const items: RssNewsItem[] = (snapshot.items ?? []).map((item) => ({
      title: item.title,
      description: item.description,
      imgUrl: item.image ? `${assetBase}/${item.image}` : defaultImageUrl,
    }));
    newsCache.set(cacheKey, { items, fetchedAt: Date.now() });
    return items;
  } catch (err) {
    console.warn(`[getFeedSnapshot:${source}] Failed to load news feed snapshot:`, err);
    return cached?.items ?? [];
  }
}

async function getNewsFeed(assetBase: string): Promise<RssNewsItem[]> {
  return getFeedSnapshot(assetBase, 'nrk', NEWS_DEFAULT_IMAGE_PATH);
}

async function getNewsFeedFhi(assetBase: string): Promise<RssNewsItem[]> {
  return getFeedSnapshot(assetBase, 'fhi');
}

export async function buildScreenView(
  _api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<ScreenView> {
  console.log(`[buildScreenView] Building screen view for UUID: ${uuid}`);

  const organization = getOrganization(state);
  assertIsDefined(organization);

  // Leger (aktivitetskode 86.21*/86.22*) får alle skjermforslag. Andre
  // grupper får alt unntatt det rent lege-spesifikke innholdet.
  const doctorOrganization = isDoctorOrganization(state);

  console.log(`[buildScreenView] Organization ID: ${organization.organizationId}`);

  try {
    const [s, signageLibrary] = await Promise.all([
      fetchScreen(Number(uuid), organization.organizationId),
      fetchLibrary(organization.organizationId).catch((err) => {
        console.warn('[buildScreenView] fetchLibrary failed:', err);
        return [];
      }),
    ]);
    console.log('[buildScreenView] Screen data fetched successfully:', s);

    const assetBase = s.previewUrl ? `https://api.trinnvis.io${s.previewUrl.replace(/\/$/, '')}/assets` : '';
    // `imageSrc` holds the canonical media reference (e.g. "std/istock-653241632.jpg").
    // URL construction is deferred to render time via resolveAssetUrl.
    const library: LibraryItem[] = signageLibrary.map((item) => ({
      id: item.id,
      name: item.name,
      imageSrc: item.assets?.[0] ?? '',
      thumbnail: item.thumbnail,
      defaultDuration: item.defaultDuration,
      standardItem: item.standardItem,
      assets: item.assets ?? [],
      signageMediaType: item.mediaType,
      slide: item.slide,
    }));

    // Reflektér library inn i asset-availability-servicen så
    // d-loading-video/d-loading-image vet hvilke paths som er trygge å
    // laste. Etter close/reopen er dette også der vi gjenoppdager pending
    // konvertering: hvis en slide har en media-path som IKKE finnes i
    // library ennå, marker den pending så servicen begynner å polle igjen.
    assetAvailability.markKnownPaths(library.flatMap((i) => i.assets ?? []));
    const knownPaths = new Set(library.flatMap((i) => i.assets ?? []));
    const playlistSlides = toSlides(s.playlistItems);
    for (const slide of playlistSlides) {
      const media = (slide as { media?: unknown }).media;
      if (typeof media !== 'string' || !media) continue;
      if (!knownPaths.has(media)) {
        assetAvailability.awaitPath(organization.organizationId, media);
      }
    }

    // Validate required fields
    if (!s.name) {
      console.warn('[buildScreenView] Screen name is missing, using default');
    }
    if (!s.playlistItems) {
      console.warn('[buildScreenView] Playlist items missing, using empty array');
    }

    // Bestillingsstatus leses nå direkte fra backend-feltet på screens-lista
    // (dabih-api #4591). Tidligere ble den avledet av [A]-prefiks i navnet +
    // playerConnected (#13126).
    const screenFromList = state.organization?.screens?.find((screen) => screen.displayId === Number(uuid));
    const orderStatus: ScreenOrderStatus = mapApiOrderStatus(screenFromList?.orderStatus);

    const rawName = s.name ?? '';

    return {
      ...commonData,
      promoData: undefined,
      docsForLinking: [],
      contentLastModified: '',
      contentLastModifiedBy: '',
      contentLastModifiedWasCreate: false,
      hasTemplateDelete: false,
      hasTemplateUpdate: false,
      templateDeclined: '',
      templateDeleted: '',
      templateDeletedMessage: '',
      templateUpdated: '',
      currentUserHasAccess: true,
      currentUserHasWriteAccess: true,
      uuid: uuid,
      isConfirmedEntity: true,
      helpContent: '',
      href: currentParent + '/screens/' + uuid,
      parentHref: currentParent,
      pdfHref: '',
      deleted: false,
      type: 'screens',
      attachments: [],
      today: LocalDate.fromString(state.today),
      deletable: orderStatus === 'none',
      hasDraft: false,
      fetchDraft() {
        throw new Error('Illegal state, not applicable');
      },
      name: rawName,
      rawName,
      orderStatus,
      assetBase,
      playlist: playlistSlides,
      templates: slideTemplates,
      suggestionsSelected: slideSuggestionsSelected(doctorOrganization),
      suggestionsCategorized: slideSuggestionsCategorizedFor(doctorOrganization),
      newsFeed: await getNewsFeed(assetBase),
      // FHI-feeden vises bare for legevirksomheter (samme audience-filter som
      // FHI-forslaget, #13227) — unngå unødvendig 404-fetch for øvrige grupper.
      newsFeedFhi: doctorOrganization ? await getNewsFeedFhi(assetBase) : [],
      media: library
        // 'slide'-items er lagrede slide-maler, ikke media-paths — hører hjemme
        // i "Mine lysbilder"-forslagene, ikke i bilde-pickeren.
        .filter((item) => item.signageMediaType !== 'presentation' && item.signageMediaType !== 'slide')
        .map((item) => item.imageSrc)
        .filter((src) => src),
      library: library,
      schedule: s.workWeek || {},
      scheduleDescription: weekScheduleDescription(s.workWeek),
      screenshotUrl: s.screenshotUrl,
      previewUrl: s.previewUrl,
      playerConnected: screenFromList?.playerConnected ?? false,
      playerCode: s.playerCode,
      employeesPresenceStatus: getEmployeesPresenceStatus(state),
    };
  } catch (error) {
    console.error('[buildScreenView] Error building screen view:', error);
    throw new Error(`Failed to load screen: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
